// @ts-nocheck

// App
import Promise from 'promise';
import api from 'app/shared/utils/api';

// Lodash
import isEmpty from 'lodash/isEmpty';

// Actions
import AuthActions from 'app/shared/flux/actions/AuthActions';

// Models
import MapData from 'app/shared/models/MapData';
import RecentSearch from 'app/shared/models/RecentSearch';
import SavedSearch from 'app/shared/models/SavedSearch';
import Filter from 'app/shared/models/Filter';

// Misc / utils
import constants from 'app/shared/constants/ConstantsBundle';
import adapterUtils from 'app/shared/utils/adapterUtils';
import gmapUtils from 'app/client/utils/map/gmapUtils';
import queryUtils from 'app/shared/utils/queryUtils';
import routeUtils from 'app/shared/utils/routeUtils';
import searchSlugUtils from 'app/shared/utils/searchSlugUtils';
import { adapt_reduxToJava, adapt_reduxToQuery } from 'app/shared/flux/actions/FilterActions/adapters';
import { areaUtils_validateMapForUpdatingRecentSearch } from 'app/shared/utils/areaUtils';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import { logError } from '@zg-rentals/log-error';
import { batch } from 'react-redux';

const logger = getGlobalLogger('actions/usersearch');

/** @type {{ key: string; promise: Promise<unknown> } | null} */
let _inFlightRecentSearchUpdate = null;
/** @type {{ dispatch: Function; apiFilter: unknown; key: string; promise: Promise<unknown>; resolve: (v: unknown) => void; reject: (e: unknown) => void } | null} */
let _queuedRecentSearchUpdate = null;
const SEARCH_SLUG_BY_PROPERTY_TYPE = {
  condo: 'condos-for-rent',
  house: 'houses-for-rent',
  townhouse: 'townhomes-for-rent',
};

const createRecentSearchFromState = ({ filter, areaInfo = {}, shouldUseAreaBoundary, mapData = {} }) => {
  const queryObj = adapt_reduxToQuery({
    filter,
    mapData: shouldUseAreaBoundary ? null : mapData,
    border: shouldUseAreaBoundary,
  });
  const searchSlug = filter.search?.slug || filter.searchSlug;
  const url =
    routeUtils.buildAreaPath({
      areaResourceId: areaInfo.resourceId,
      searchSlug,
    }) + queryUtils.stringify(queryObj);

  return {
    url,
    recentSearch: new RecentSearch({
      hasBoundary: shouldUseAreaBoundary,
      resourceId: areaInfo.resourceId,
      filter,
      queryObj,
      mapData,
      areaInfo,
    }),
  };
};

// Key only needs to be consistent within a single page session (same JS engine,
// same object insertion order), so JSON.stringify is stable enough for dedup.
const createRecentSearchUpdateKey = ({ apiFilter, recentSearchUrl }) => {
  return recentSearchUrl || JSON.stringify(apiFilter || {});
};

const findPendingRecentSearchUpdate = (requestKey) => {
  if (_inFlightRecentSearchUpdate?.key === requestKey) return _inFlightRecentSearchUpdate;
  if (_queuedRecentSearchUpdate?.key === requestKey) return _queuedRecentSearchUpdate;
  return null;
};

const drainQueuedRecentSearchUpdate = () => {
  if (!_queuedRecentSearchUpdate) {
    return Promise.resolve();
  }

  const queuedRecentSearchUpdate = _queuedRecentSearchUpdate;
  _queuedRecentSearchUpdate = null;

  return startRecentSearchUpdate({
    dispatch: queuedRecentSearchUpdate.dispatch,
    apiFilter: queuedRecentSearchUpdate.apiFilter,
    requestKey: queuedRecentSearchUpdate.key,
  }).then(queuedRecentSearchUpdate.resolve, queuedRecentSearchUpdate.reject);
};

const finalizeRecentSearchUpdate = (requestKey) => {
  if (_inFlightRecentSearchUpdate?.key === requestKey) {
    _inFlightRecentSearchUpdate = null;
  }

  return drainQueuedRecentSearchUpdate();
};

const startRecentSearchUpdate = ({ dispatch, apiFilter, requestKey }) => {
  const promise = dispatch(api.user.search.recent.update({ filter: apiFilter })).then(
    (result) => finalizeRecentSearchUpdate(requestKey).then(() => result),
    (error) =>
      finalizeRecentSearchUpdate(requestKey).then(() => {
        throw error;
      }),
  );

  _inFlightRecentSearchUpdate = {
    key: requestKey,
    promise,
  };

  return promise;
};

const queueRecentSearchUpdate = ({ dispatch, apiFilter, requestKey }) => {
  if (_queuedRecentSearchUpdate) {
    _queuedRecentSearchUpdate.dispatch = dispatch;
    _queuedRecentSearchUpdate.apiFilter = apiFilter;
    _queuedRecentSearchUpdate.key = requestKey;
    return _queuedRecentSearchUpdate.promise;
  }

  let resolveQueuedRecentSearchUpdate;
  let rejectQueuedRecentSearchUpdate;
  const promise = new Promise((resolve, reject) => {
    resolveQueuedRecentSearchUpdate = resolve;
    rejectQueuedRecentSearchUpdate = reject;
  });

  _queuedRecentSearchUpdate = {
    dispatch,
    apiFilter,
    key: requestKey,
    promise,
    resolve: resolveQueuedRecentSearchUpdate,
    reject: rejectQueuedRecentSearchUpdate,
  };

  return promise;
};

const dispatchRecentSearchUpdate = ({ dispatch, apiFilter, requestKey }) => {
  const pending = findPendingRecentSearchUpdate(requestKey);
  if (pending) {
    return pending.promise;
  }

  if (_inFlightRecentSearchUpdate) {
    return queueRecentSearchUpdate({ dispatch, apiFilter, requestKey });
  }

  return startRecentSearchUpdate({ dispatch, apiFilter, requestKey });
};

const buildRecentSearchUpdateRequest = ({ getState, mapDataInfo, newFilter }) => {
  const { filter, area, location, app, user } = getState();
  const isMobile = app.device.isMobile;
  const shouldUseAreaBoundary = location.current.query.border !== false;
  const effectiveFilter = newFilter ? newFilter : filter;

  // mapData precedence: live map -> caller-provided -> area bounds fallback.
  // HP-7973: fall back to the loaded area's bounds when the Google map hasn't
  // initialized yet, so desktop unbounded searches still produce a useful
  // viewport. Previously this case (no map, no boundary, desktop) returned
  // null and recommendation emails never saw the user's filter.
  const liveMapData = app.gmapLoaded && gmapUtils.getMapData(window.map);
  const areaHasBounds = !isEmpty(area.area) && area.area.minLat != null && area.area.maxLat != null;
  const areaBoundsFallback = areaHasBounds
    ? {
        minLat: area.area.minLat,
        minLon: area.area.minLon,
        maxLat: area.area.maxLat,
        maxLon: area.area.maxLon,
      }
    : null;
  const mapData = liveMapData || mapDataInfo || areaBoundsFallback || {};

  // Bail only for desktop unbounded searches with no viewport at all (no live
  // map, no caller data, no area bounds). Mobile and area-bounded searches
  // still proceed with whatever they have, as before.
  if (isEmpty(mapData) && !isMobile && !shouldUseAreaBoundary) {
    return null;
  }

  const areaForApi =
    shouldUseAreaBoundary && !isEmpty(area.area)
      ? {
          id: area.area.id,
          maxLat: area.area.maxLat,
          minLat: area.area.minLat,
          maxLon: area.area.maxLon,
          minLon: area.area.minLon,
        }
      : null;
  const apiFilter = adapt_reduxToJava({
    filter: effectiveFilter,
    area: areaForApi,
    mapData,
  });
  const candidateRecentSearch = createRecentSearchFromState({
    filter: effectiveFilter,
    areaInfo: area.area,
    shouldUseAreaBoundary,
    mapData,
  });

  return {
    apiFilter,
    requestKey: createRecentSearchUpdateKey({
      apiFilter,
      recentSearchUrl: candidateRecentSearch.url,
    }),
    recentSearch: candidateRecentSearch.recentSearch,
    recentSearchUrl: candidateRecentSearch.url,
    currentRecentSearch: user?.search?.recent,
  };
};

// The recent search API response often lacks searchSlug/searchTitle, which causes the
// RecentSearch model to build a URL with the wrong slug (defaulting to apartments-for-rent).
// This derives the correct slug from propertyTypes when possible, falling back to the
// current Redux filter's slug to keep the recent search URL consistent with the active page.
const normalizeRecentSearchFilter = ({ filter = {}, fallbackSearch = {}, currentFilterSearch = {} }) => {
  const explicitSearchSlug = filter.searchSlug || filter.search?.slug;
  const explicitSearchTitle = filter.searchTitle || filter.search?.title;
  const fallbackSearchSlug = fallbackSearch?.slug;
  const fallbackSearchTitle = fallbackSearch?.title;
  const currentFilterSearchSlug = currentFilterSearch?.slug;
  const currentFilterSearchTitle = currentFilterSearch?.title;
  const activePropertyTypes = String(filter.propertyTypes || '')
    .split(',')
    .filter(Boolean);
  const isSinglePropertyType = activePropertyTypes.length === 1;
  const derivedSearchSlug = isSinglePropertyType ? SEARCH_SLUG_BY_PROPERTY_TYPE[activePropertyTypes[0]] : null;
  const searchSlug = explicitSearchSlug || fallbackSearchSlug || derivedSearchSlug || currentFilterSearchSlug;
  const matchedFallbackSearchTitle = fallbackSearchSlug === searchSlug ? fallbackSearchTitle : null;
  const matchedCurrentFilterSearchTitle = currentFilterSearchSlug === searchSlug ? currentFilterSearchTitle : null;

  if (!searchSlug) {
    return filter;
  }

  return {
    ...filter,
    searchSlug,
    searchTitle:
      explicitSearchTitle ||
      matchedFallbackSearchTitle ||
      matchedCurrentFilterSearchTitle ||
      searchSlugUtils.prepareSlugDescription(searchSlug),
  };
};

const UserSearchActions = {
  search: {
    saved: {
      create(name, email, frequency, useMapBounds, borderAreaId) {
        return function (dispatch, getState) {
          const { filter, area, user, location } = getState();
          const { minLat, minLon, maxLat, maxLon } = area.area;
          let mapData = { minLat, minLon, maxLat, maxLon };

          if (!email) {
            logger?.warn('UserSearchActions#search#saved No email specified.');
            return Promise.reject(new Error('No email specified'));
          }

          if (useMapBounds && window.map) {
            mapData = gmapUtils.getMapData(window.map);
          }

          const savedSearchItem = adapterUtils.savedSearchItemForApi({
            name,
            email,
            frequency,
            filter,
            mapData,
            borderAreaId,
          });

          if (!savedSearchItem) {
            logError({ error: 'Error in UserSearchActions#search.saved.create', errorType: 'UserSearchActionsError' });
            return Promise.reject(new Error('Error creating saved search item'));
          }

          if (user.loggedIn) {
            return dispatch(api.user.search.saved.create(savedSearchItem));
          } else {
            const url = routeUtils.buildFreepassPath(location.current);
            return dispatch(api.user.login.freepass({ email, url }))
              .then((result) => {
                if (result.success && result.loggedIn) {
                  dispatch(AuthActions.login.success(result));
                  result.loggedIn = true;
                  return result;
                } else {
                  result.loggedIn = false;
                  return result;
                }
              })
              .then((result) => {
                if (result.loggedIn) {
                  return dispatch(api.user.search.saved.create(savedSearchItem));
                } else if (result.status === 'USER_ALREADY_EXISTS') {
                  return result;
                }

                throw new Error('Login failed');
              });
          }
        };
      },
      update({ id, name, frequency, filter, mapData, borderAreaId }) {
        return function (dispatch) {
          if (!id) {
            logger?.warn('UserSearchActions#search#update No id specified.');
            return false;
          }

          const updatedSearchItem = adapterUtils.savedSearchItemForApi({
            id,
            name,
            frequency,
            filter,
            mapData,
            borderAreaId,
          });

          if (!updatedSearchItem) {
            logError({ error: 'Error in UserSearchActions#search.saved.update', errorType: 'UserSearchActionsError' });
            return false;
          }

          return dispatch(api.user.search.saved.update(updatedSearchItem));
        };
      },
      delete(searchId) {
        return function (dispatch) {
          if (!searchId) {
            logger?.warn('UserSearchActions#search#delete No searchId specified.');
            return false;
          }

          return dispatch(api.user.search.saved.delete(searchId)).then((result) => {
            if (result.success === true) {
              dispatch(UserSearchActions.getSavedSearches());
            }
            return result;
          });
        };
      },
      displaySearchPromptInquiry({ shouldUseAreaBoundary, useMapBounds }) {
        return function (dispatch, getState) {
          const area = getState().area.area;
          let mapData = {
            minLat: area.minLat,
            minLon: area.minLon,
            maxLat: area.maxLat,
            maxLon: area.maxLon,
          };

          if (useMapBounds && window.map) {
            mapData = gmapUtils.getMapData(window.map);
          }

          return dispatch(
            api.user.search.saved.displaySearchPromptInquiry({
              minLat: mapData.minLat,
              minLon: mapData.minLon,
              maxLat: mapData.maxLat,
              maxLon: mapData.maxLon,
              areas: shouldUseAreaBoundary ? area.id : null,
            }),
          ).then((res) => {
            return res;
          });
        };
      },
    },
    recommended: {
      get() {
        return function (dispatch) {
          return dispatch(api.user.search.recommended.get());
        };
      },
      reset(id) {
        return function (dispatch) {
          return dispatch(api.user.search.recommended.reset(id));
        };
      },
      reactivate(email) {
        return function (dispatch) {
          return dispatch(api.user.search.recommended.reactivate(email));
        };
      },
    },
  },
  getSavedSearches() {
    return function (dispatch) {
      return dispatch(api.user.search.saved.get()).then((result) => {
        const savedSearchesMap = result.data.map((data) => {
          const {
            sid = null,
            filter = {},
            resourceId = 'new-york-ny',
            created,
            name,
            emailFrequency,
            numberOfNewResults,
          } = data;
          const mapData = new MapData(filter);
          const createdFilter = new Filter(filter);
          const queryObj = adapt_reduxToQuery({
            filter: createdFilter,
            mapData: filter.areas ? null : mapData,
            border: filter.areas || false,
          });

          return new SavedSearch({
            sid,
            filter: createdFilter,
            resourceId,
            created,
            name,
            emailFrequency,
            numberOfNewResults,
            queryObj,
            mapData,
            borderAreaId: filter.areas,
          });
        });

        dispatch({
          type: constants.UPDATE_SAVED_SEARCHES,
          payload: {
            savedSearch: savedSearchesMap,
          },
        });

        return;
      });
    };
  },
  getRecentSearches() {
    return function (dispatch, getState) {
      return dispatch(api.user.search.recent.get())
        .then((result = {}) => {
          if (!result.data) {
            return;
          }

          const { filter = {}, areaInfo = {} } = result.data;
          const fallbackSearch = getState().user?.search?.recent?.filter?.search;
          const currentFilterSearch = getState().filter?.search;
          const normalizedFilter = normalizeRecentSearchFilter({
            filter,
            fallbackSearch,
            currentFilterSearch,
          });
          const hasBoundary = Boolean(normalizedFilter.areas);
          const mapData = new MapData(normalizedFilter);
          const createdFilter = new Filter(normalizedFilter);
          const queryObj = adapt_reduxToQuery({
            filter: createdFilter,
            mapData: hasBoundary ? null : mapData,
            border: hasBoundary,
          });

          return new RecentSearch({
            hasBoundary,
            resourceId: areaInfo.resourceId,
            filter: createdFilter,
            queryObj,
            mapData,
            areaInfo,
          });
        })
        .then((recentSearch) => {
          const recentSearchSlug = recentSearch?.filter?.search?.slug;
          if (
            recentSearch?.filterDescriptionV2 &&
            recentSearchSlug &&
            !recentSearch.url.includes(`/${recentSearchSlug}`)
          ) {
            logger?.warn('UserSearchActions#getRecentSearches recent search URL does not match filter slug', {
              recentSearchUrl: recentSearch.url,
              recentSearchSlug,
              recentSearchPropertyTypes: recentSearch.filter?.propertyTypes,
              areaResourceId: recentSearch.areaInfo?.resourceId,
            });
          }

          dispatch({
            type: constants.UPDATE_RECENT_SEARCHES,
            payload: {
              recentSearch,
            },
          });
          return recentSearch;
        })
        .catch((err) =>
          logError({ error: err, errorType: 'UserSearchActionsError', context: 'Error getting recent searches' }),
        );
    };
  },
  updateRecentSearch({ mapDataInfo, newFilter } = {}) {
    return function (dispatch, getState) {
      if (__BROWSER__) {
        const recentSearchUpdateRequest = buildRecentSearchUpdateRequest({
          getState,
          mapDataInfo,
          newFilter,
        });

        if (!recentSearchUpdateRequest) {
          return Promise.resolve();
        }

        const { apiFilter, requestKey, recentSearch, recentSearchUrl, currentRecentSearch } = recentSearchUpdateRequest;
        const pending = findPendingRecentSearchUpdate(requestKey);

        // URL already matches — skip the API call. Returns the existing
        // RecentSearch (not void) so callers must not depend on the resolved type.
        if (currentRecentSearch?.url && currentRecentSearch.url === recentSearchUrl) {
          dispatch({
            type: constants.SET_CURRENT_SEARCH,
            payload: null,
          });

          return pending?.promise || Promise.resolve(currentRecentSearch);
        }

        batch(() => {
          dispatch({
            type: constants.SET_CURRENT_SEARCH,
            payload: null,
          });
          dispatch({
            type: constants.UPDATE_RECENT_SEARCHES,
            payload: {
              recentSearch,
            },
          });
        });

        return pending?.promise || dispatchRecentSearchUpdate({ dispatch, apiFilter, requestKey });
      }
    };
  },
  setCurrentSearch({ mapData, waiting, resourceId, isMapFirstIdle = false }) {
    return function (dispatch, getState) {
      const { filter, area, location } = getState();
      const currentSearch = getState().user.currentSearch;
      const currentSearchReady = currentSearch && currentSearch.waiting;
      const currAreaMatches = area.area && currentSearch && currentSearch.resourceId === area.area.resourceId;

      if (waiting) {
        // case 1: user searched (no map data)
        return dispatch({
          type: constants.SET_CURRENT_SEARCH,
          payload: { waiting: true, resourceId },
        });
      } else if (currentSearchReady && currAreaMatches) {
        // case 2: map data came back - update recent search
        dispatch(UserSearchActions.updateRecentSearch({ mapDataInfo: mapData }));
      } else if (currentSearchReady && !currAreaMatches) {
        // do nothing, wait till area is done loading
      } else if (
        areaUtils_validateMapForUpdatingRecentSearch(mapData, location.current.query.border) &&
        !isMapFirstIdle
      ) {
        // case 3: save api filter info for when user qualifies search to be a recent search
        // ie clicks on a listing
        const shouldUseAreaBoundary = location.current.query.border !== false;
        const areaId = shouldUseAreaBoundary ? area.area.id : null;
        const apiFilter = adapt_reduxToJava({
          filter,
          area: { id: areaId },
          mapData,
        });
        return dispatch({
          type: constants.SET_CURRENT_SEARCH,
          payload: { apiFilter },
        });
      } else {
        // invalid search (ie too zoomed out)
        // set current search to null to avoid updating recent search
        return dispatch({
          type: constants.SET_CURRENT_SEARCH,
          payload: null,
        });
      }
    };
  },
  listingOnCurrentSearchClicked() {
    return function (dispatch, getState) {
      const currentSearch = getState().user.currentSearch;
      if (currentSearch && currentSearch.apiFilter) {
        dispatch({
          type: constants.SET_CURRENT_SEARCH,
          payload: null,
        });

        return dispatchRecentSearchUpdate({
          dispatch,
          apiFilter: currentSearch.apiFilter,
          requestKey: createRecentSearchUpdateKey({ apiFilter: currentSearch.apiFilter }),
        });
      }
    };
  },
};

// Exposed for testing only — resets recent search request state between test runs.
export const _resetUpdateRecentSearchInFlight = () => {
  _inFlightRecentSearchUpdate = null;
  _queuedRecentSearchUpdate = null;
};

export default UserSearchActions;
